import React, { createContext, useContext, useEffect, useMemo, useState, useRef } from 'react';
import { detectMode, type Mode, type ConnSnapshot } from './detect';
import { logConnMode, tryFlushConnLogsSoon } from '@/analytics/connModeLogger';
import { applyDynamicCSP, checkAndApplyPendingStrictCSP } from '@/utils/strictCSP';
import { toast } from '@/hooks/use-toast';
import { initFontOptimization } from '@/utils/fontOptimization';
import { shouldAutoEnableLiteMode, getNetworkInfo, getNetworkDescription } from './networkUtils';
import { getKillSwitches, initKillSwitches } from '@/utils/killSwitches';

// Import video debugger for development
if (import.meta.env.DEV) {
  import('@/utils/videoDebugger').then(({ runVideoDebugCheck }) => {
    (window as any).debugVideos = runVideoDebugCheck;
    console.log('💡 Video debugging available: run debugVideos() in console');
  });
  
  import('@/utils/videoClickFix').then(({ debugVideoClicks }) => {
    (window as any).debugVideoClicks = debugVideoClicks;
    console.log('💡 Video click debugging available: run debugVideoClicks() in console');
  });
}

const ModeCtx = createContext<Mode>('full');
export const useConnectionMode = () => useContext(ModeCtx);

function snapshot(): ConnSnapshot {
  // SSR-safe checks
  if (typeof window === 'undefined') {
    return { online: true };
  }
  
  const conn: any = (navigator as any).connection || {};
  return {
    online: navigator.onLine,
    effectiveType: conn.effectiveType,
    downlink: typeof conn.downlink === 'number' ? conn.downlink : undefined,
    saveData: !!conn.saveData,
  };
}

// Network-aware sticky mode detection with user override and kill switches
function getInitialMode(): Mode {
  // SSR safety
  if (typeof window === 'undefined') return 'full';
  
  // Check kill switches first (URL params override everything)
  const switches = getKillSwitches();
  if (switches.forceLiteMode) {
    console.debug('[Connection] Kill switch: forcing lite mode');
    return 'lite';
  }
  if (switches.forceFullMode) {
    console.debug('[Connection] Kill switch: forcing full mode');
    return 'full';
  }
  
  // Check for explicit user override
  const storedMode = localStorage.getItem('mode') as Mode | null;
  if (storedMode && ['full', 'balanced', 'lite', 'offline'].includes(storedMode)) {
    console.debug('[Connection] Using stored user preference:', storedMode);
    return storedMode;
  }
  
  // FIELD-READY DEFAULT: LiteMode as baseline for better performance
  // Network-aware auto-detection with lite as conservative default
  const autoLite = shouldAutoEnableLiteMode();
  const autoDetected: Mode = !navigator.onLine ? 'offline' : (autoLite ? 'lite' : 'lite'); // Default to lite
  
  // Store the auto-detected mode as the initial preference if none exists
  if (!storedMode) {
    localStorage.setItem('mode', autoDetected);
    console.debug('[Connection] Auto-detected and stored initial mode (lite baseline):', autoDetected, {
      networkDescription: getNetworkDescription(),
      networkInfo: getNetworkInfo()
    });
  }
  
  return autoDetected;
}

export function SimpleConnectionProvider({ children }: { children: React.ReactNode }) {
  const [mode, setMode] = useState<Mode>('full');
  const previousModeRef = useRef<Mode | null>(null); // null indicates initial load
  const lastNotificationRef = useRef<number>(0);
  const NOTIFICATION_COOLDOWN = 30000; // 30 seconds between notifications

  // Helper to determine if a mode change is a significant downgrade worth notifying about
  const isSignificantDowngrade = (from: Mode, to: Mode): boolean => {
    const modeRanking = { 'full': 4, 'balanced': 3, 'lite': 2, 'offline': 1 };
    const fromRank = modeRanking[from];
    const toRank = modeRanking[to];
    
    // Only notify for downgrades of 2+ levels (Full->Lite, Full->Offline, Balanced->Offline)
    // or any downgrade to offline
    return (fromRank - toRank >= 2) || to === 'offline';
  };

  // Show downgrade notification with appropriate messaging
  const showDowngradeNotification = (from: Mode, to: Mode) => {
    const now = Date.now();
    
    // Debounce notifications to avoid spam
    if (now - lastNotificationRef.current < NOTIFICATION_COOLDOWN) {
      return;
    }
    
    lastNotificationRef.current = now;
    
    const messages = {
      'offline': {
        title: '📡 You\'re now offline',
        description: 'Some features are limited. Content will sync when reconnected.'
      },
      'lite': {
        title: '🌍 Lite Mode Auto-Enabled',
        description: 'Slow connection or data saver detected. Optimized for your network. You can manually override in settings.'
      }
    };
    
    const message = messages[to];
    if (message) {
      toast({
        title: message.title,
        description: message.description,
        duration: 5000, // 5 seconds
      });
    }
  };

  // One function to apply mode to DOM + state + CSP + notifications
  const apply = (m: Mode) => {
    const previousMode = previousModeRef.current;
    
    // Only show notification if this is a significant downgrade and not the initial load
    if (previousMode !== null && previousMode !== m && isSignificantDowngrade(previousMode, m)) {
      showDowngradeNotification(previousMode, m);
    }
    
    previousModeRef.current = m;
    setMode(m);
    
    // Initialize font optimization strategy
    initFontOptimization(m);
    
    if (typeof window !== 'undefined') {
      const el = document.documentElement;
      el.dataset.mode = m;
      el.classList.toggle('lite', m === 'lite');
      
      // Apply dynamic CSP based on connection mode
      applyDynamicCSP(m);
      
      // Mode telemetry for debugging including network-aware detection
      console.debug('[conn-mode] Applied mode:', m, {
        CSPUpdated: true,
        networkConditions: {
          online: navigator.onLine,
          effectiveType: (navigator as any).connection?.effectiveType,
          saveData: (navigator as any).connection?.saveData,
          downlink: (navigator as any).connection?.downlink
        },
        isStored: !!localStorage.getItem('mode')
      });
      (window as any).dataLayer?.push({ event: 'conn_mode', mode: m });
      
      // Log (skip in development if you want)
      if (import.meta.env.PROD) logConnMode(m);
    }
  };

  useEffect(() => {
    // Initialize kill switches first (for URL param parsing and remote config)
    initKillSwitches();
    
    // Check if Service Worker should be disabled via kill switch
    const switches = getKillSwitches();
    if (!switches.disableServiceWorker) {
      checkAndApplyPendingStrictCSP();
    } else {
      console.debug('[Connection] Service Worker disabled via kill switch');
    }
    
    // Debug current state including network-aware detection and kill switches
    console.debug('[Connection] Initial state check:', {
      onLine: navigator.onLine,
      connection: (navigator as any).connection,
      effectiveType: (navigator as any).connection?.effectiveType,
      saveData: (navigator as any).connection?.saveData,
      storedMode: localStorage.getItem('mode'),
      killSwitches: switches,
      hasStrictCSPPending: document.documentElement.hasAttribute('data-strict-csp-pending'),
      currentCSP: document.querySelector('meta[http-equiv="Content-Security-Policy"]')?.getAttribute('content')?.substring(0, 100)
    });
    
    // After initial apply, attempt to flush any queued events
    tryFlushConnLogsSoon();
  }, []);

  useEffect(() => {
    // SSR safety check
    if (typeof window === 'undefined') return;
    
    // Initialize with network-aware sticky mode
    const initialMode = getInitialMode();
    apply(initialMode);

    // React to online/offline and connection changes
    const onOnline  = () => {
      // When coming back online, respect user override or re-detect
      const storedMode = localStorage.getItem('mode') as Mode | null;
      if (storedMode && storedMode !== 'offline') {
        apply(storedMode);
      } else {
        const networkMode = getInitialMode();
        apply(networkMode);
      }
      tryFlushConnLogsSoon(); // Flush queued analytics when back online
    };
    
    const onOffline = () => apply('offline');
    
    const onChange = () => {
      // On connection change, only auto-adjust if user hasn't manually overridden
      const storedMode = localStorage.getItem('mode') as Mode | null;
      
      // If user has explicitly set a mode, don't auto-change unless going offline
      if (storedMode && navigator.onLine) {
        return; // Respect user override
      }
      
      // Auto-detect for new connection conditions
      const networkMode = getInitialMode();
      apply(networkMode);
    };
    
    const conn: any = (navigator as any).connection;

    // Add listeners
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    if (conn && typeof conn.addEventListener === 'function') {
      conn.addEventListener('change', onChange);
    }

    // Cleanup function - guaranteed to remove all listeners
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      conn?.removeEventListener?.('change', onChange);
    };
  }, []);

  // Stable context value
  const value = useMemo(() => mode, [mode]);
  return <ModeCtx.Provider value={value}>{children}</ModeCtx.Provider>;
}